import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';
import { useSelector } from 'react-redux';
import { Link, Outlet, useNavigate, useParams } from 'react-router-dom';

import { customerSelector, useGetContactsQuery, useGetFiatAccountsQuery } from '@/api/v1';
import { Clickable } from '@/components/atoms/Clickable';
import { ContactItem } from '@/components/molecules/ContactItem';
import { SearchBox } from '@/components/molecules/SearchBox';
import { MainScreen } from '@/components/organisms/MainScreen';
import { useTranslator } from '@/hooks/useTranslator';
import { ContactsDrawerContext } from '@/types/contactDrawerContext';
import { Contact, FiatAccount } from '@/types/contacts';
import { Customer } from '@/types/customer';
import { Page } from '@/types/page';
import { isEmptyNullOrUndefined } from '@/utility/isEmptyNullOrUndefined';

export const Contacts: React.FC = (): React.ReactElement => {
  const { data: customer = Customer.empty() } = useSelector(customerSelector);
  const { data: accountsPage = Page.empty<FiatAccount>() } = useGetFiatAccountsQuery(
    customer.beneficiaryId,
    {
      skip: isEmptyNullOrUndefined(customer.beneficiaryId),
    },
  );
  const { data: recipients = [] } = useGetContactsQuery(customer.beneficiaryId);
  const { accountId, contactId } = useParams<ContactsDrawerContext>();

  const navigate = useNavigate();
  const tr = useTranslator();

  const accounts = React.useMemo((): readonly FiatAccount[] => {
    const { content } = accountsPage;
    return content.filter((account: FiatAccount): boolean => account.belongsToBeneficiary);
  }, [accountsPage]);

  const onAccountClick = React.useCallback(
    (accountId: string): void => {
      navigate(`account/${accountId}/details`);
    },
    [navigate],
  );

  const onRecipientClick = React.useCallback(
    (recipientId: string): void => {
      navigate(`recipient/${recipientId}/details`);
    },
    [navigate],
  );

  const outletContext = React.useMemo(
    (): ContactsDrawerContext => ({ contactId, accountId }),
    [accountId, contactId],
  );

  return (
    <MainScreen title={tr('CONTACTS')}>
      <Box position="absolute" top={0} right={0}>
        <Button variant="contained" component={Link} to="new">
          {tr('ADD_NEW')}
        </Button>
      </Box>
      <Box display="flex" flexDirection="column" gap={3} mt={1}>
        <Paper elevation={1}>
          <Box px={4} py={5}>
            <Box pb={4}>
              <Typography variant="h6">{tr('YOUR_ACCOUNTS')}</Typography>
            </Box>
            <Box sx={styles.accountsGrid}>
              {accounts.map(
                (account: FiatAccount): React.ReactElement => (
                  <Clickable key={account.id} data={account.id} onClick={onAccountClick}>
                    <ContactItem accounts={[account]} title={account.name} />
                  </Clickable>
                ),
              )}
            </Box>
          </Box>
        </Paper>
        <Paper elevation={1}>
          <Box px={4} py={5}>
            <Box display="flex" alignItems="center" justifyContent="space-between" pb={4}>
              <Typography variant="h6">{tr('RECIPIENTS')}</Typography>
              <SearchBox />
            </Box>
            <Box sx={styles.recipientsGrid}>
              {recipients.map(
                (contact: Contact): React.ReactElement => (
                  <Clickable key={contact.id} data={contact.id} onClick={onRecipientClick}>
                    <ContactItem
                      key={contact.id}
                      accounts={contact.accounts}
                      title={Contact.getName(contact)}
                    />
                  </Clickable>
                ),
              )}
            </Box>
          </Box>
        </Paper>
        <Box height={30} />
      </Box>

      {/* For modals */}
      <Outlet context={outletContext} />
    </MainScreen>
  );
};

const styles: Record<string, SystemStyleObject> = {
  recipientsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 2,
  },
  accountsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: 2,
  },
};
